import React, { useCallback, type ReactNode } from 'react';
import type { UseVirtualRowsReturn, VirtualRowGroup } from './useVirtualRows';

export interface VirtualTbodyProps {
  virtualRows: UseVirtualRowsReturn;
  /** Total number of columns (for spacer tr colSpan) */
  colSpan: number;
  /**
   * Render callback for each visible row group.
   * Receives the row group data and the virtual index.
   */
  renderRow: (
    group: VirtualRowGroup,
    virtualIndex: number,
  ) => ReactNode;
}

const TBODY_RESET: React.CSSProperties = {
  padding: 0,
  margin: 0,
  border: 'none',
  borderSpacing: 0,
};

const SPACER_TD: React.CSSProperties = {
  padding: 0,
  margin: 0,
  border: 'none',
  lineHeight: 0,
  fontSize: 0,
};

/**
 * VirtualTbody renders multiple <tbody> elements within the same <table>.
 * Each virtual row group gets its own <tbody> for height measurement.
 *
 * All <tbody> wrappers have explicit inline style resets to prevent
 * browser-default spacing that causes cumulative height drift.
 *
 * Spacer <tbody> elements at top/bottom simulate the full table height
 * for the scrollbar, keeping native table semantics intact.
 */
export const VirtualTbody: React.FC<VirtualTbodyProps> = ({
  virtualRows,
  colSpan,
  renderRow,
}) => {
  const { virtualItems, totalSize, getRowGroup, virtualizer } = virtualRows;

  const measureRef = useCallback(
    (el: HTMLElement | null) => {
      if (el) virtualizer.measureElement(el);
    },
    [virtualizer],
  );

  if (virtualItems.length === 0) return <tbody />;

  const firstItem = virtualItems[0];
  const lastItem = virtualItems[virtualItems.length - 1];
  const paddingTop = firstItem.start;
  const paddingBottom = Math.max(0, totalSize - (lastItem.start + lastItem.size));

  return (
    <>
      {paddingTop > 0 && (
        <tbody data-virtual-spacer="top" style={TBODY_RESET}>
          <tr>
            <td colSpan={colSpan} style={{ ...SPACER_TD, height: paddingTop }} />
          </tr>
        </tbody>
      )}
      {virtualItems.map((vItem) => {
        const group = getRowGroup(vItem.index);
        if (!group) return null;
        return (
          <tbody
            key={vItem.index}
            data-index={vItem.index}
            style={TBODY_RESET}
            ref={measureRef}
          >
            {renderRow(group, vItem.index)}
          </tbody>
        );
      })}
      {paddingBottom > 0 && (
        <tbody data-virtual-spacer="bottom" style={TBODY_RESET}>
          <tr>
            <td colSpan={colSpan} style={{ ...SPACER_TD, height: paddingBottom }} />
          </tr>
        </tbody>
      )}
    </>
  );
};

VirtualTbody.displayName = 'VirtualTbody';
